import { mapTransKeyToLocale } from './localization';

/**
 * A locale is an identifier used to get language, culture, or regionally-specific behavior.
 *
 * Examples of identifiers include:
 *  - en-US (English, United States)
 *  - fr-FR (French for France)
 */
export class Locale {
  private language: string;
  private region: string;

  /**
   * @param language the language code
   * @param region the region code
   */
  private constructor(language: string, region?: string) {
    if (region !== undefined) {
      this.region = region;
    }
    this.language = language;
  }

  /**
   * Returns the language code of this locale.
   * @returns the language code, or `undefined` if none is defined.
   */
  getLanguage(): string {
    return this.language;
  }

  /**
   * Returns the region for this locale.
   * @returns the region code, or `undefined` if none is defined.
   */
  getRegion(): string {
    return this.region;
  }

  /**
   * Returns a string representation of this locale.
   * @return the locale code
   */
  toString(): string {
    const parts: Array<string> = [];

    if (this.language !== undefined) {
      parts.push(this.language);
    }
    if (this.region !== undefined) {
      parts.push(this.region);
    }

    return parts.join('-');
  }

  /**
   * Gets a locale from its identifier.
   * @param locale the locale identifier
   * @return the locale
   */
  static parse(locale: string): Locale {
    const localeParts: Array<string> = locale.split('-');

    if (localeParts.length === 0) {
      throw new Error(`Invalid locale ${locale}`);
    }

    return new Locale(localeParts[0], localeParts[1] || undefined);
  }

  /**
   * Gets a locale from translation key.
   * @param key language code in legacy format.
   */
  static fromTranslationKey(key: string): Locale {
    return Locale.parse(mapTransKeyToLocale(key));
  }
}
